import React, { useEffect, useState, useCallback } from "react";
import { DndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  Checkbox,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import PrintIcon from "@mui/icons-material/Print";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReplayIcon from "@mui/icons-material/Replay";

import {
  collection,
  doc,
  getDocs,
  query,
  where,
  setDoc,
  updateDoc,
  addDoc,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";

import IndividualsScoreSheet from "../scoring/IndividualsScoreSheet";
import TeamsScoreSheet from "../scoring/TeamsScoreSheet";

// --- Utility functions ---
function getDrawMatchId({ pId, weekIndex, matchIndex }) {
  return `pennant-${pId}-week-${weekIndex}-match-${matchIndex}`;
}

function buildSlotId({ pId, weekIndex, matchIndex, side }) {
  return `pennant-${pId}-week-${weekIndex}-match-${matchIndex}-side-${side}`;
}

function parseSlotId(slotId) {
  const parts = slotId.split("-");
  if (parts.length < 8) return null;
  return {
    pId: parts[1],
    weekIndex: Number(parts[3]),
    matchIndex: Number(parts[5]),
    side: parts[7]
  };
}

function formatDate(dt) {
  const dd = String(dt.getDate()).padStart(2, "0");
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const yyyy = dt.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function formatPhoneNumber(phone) {
  if (!phone) return "";
  if (phone.startsWith("+61")) {
    return `0${phone.slice(3)}`;
  }
  return phone;
}

function getPlayersForSide(
  pennant,
  sideObj,
  teamsByPennant,
  globalMembersMap,
  weekIndex,
  matchIndex,
  sideLabel
) {
  const pId = pennant.id;
  if (sideObj?.name?.toLowerCase() === "bye") {
    if (!sideObj.id) {
      sideObj.id = `bye-${pId}-w${weekIndex}-m${matchIndex}-${sideLabel}`;
    }
    return [
      {
        id: sideObj.id,
        firstName: "BYE",
        lastName: "(Replace?)",
        phoneNumber: "",
        email: ""
      }
    ];
  }
  if (!sideObj?.id) {
    return [];
  }
  const pt = pennant.participantType;
  const isSingles = pt === "Individuals" && pennant.playType === "Singles";
  if (isSingles) {
    const maybeMemObj = globalMembersMap[sideObj.id];
    if (!maybeMemObj) return [];
    return [{ id: sideObj.id, ...maybeMemObj }];
  }
  const pennantTeams = teamsByPennant[pId] || {};
  const thisTeamDoc = pennantTeams[sideObj.id];
  if (!thisTeamDoc || !thisTeamDoc.members) return [];
  const membersObj = thisTeamDoc.members;
  return Object.values(membersObj).map((m) => {
    const global = globalMembersMap[m.id] || {};
    return {
      id: m.id,
      firstName: m.firstName ?? global.firstName ?? "Unknown",
      lastName: m.lastName ?? global.lastName ?? "Member",
      phoneNumber: m.phoneNumber ?? global.phoneNumber ?? "",
      email: m.email ?? global.email ?? ""
    };
  });
}

// --- UI Components ---
function PrintButton({ pId, onPrint }) {
  const handleClick = useCallback(() => {
    onPrint(pId);
  }, [onPrint, pId]);
  return (
    <Button size="small" variant="contained" startIcon={<PrintIcon />} onClick={handleClick}>
      Print
    </Button>
  );
}

function ScoresButton({ onScores }) {
  return (
    <Button size="small" variant="outlined" onClick={onScores}>
      Scores
    </Button>
  );
}

function FillInsButton({ pId, onOpenFillIns }) {
  const handleClick = useCallback(() => {
    onOpenFillIns(pId);
  }, [onOpenFillIns, pId]);
  return (
    <Button size="small" variant="outlined" onClick={handleClick}>
      Available Fill-Ins
    </Button>
  );
}

function DnDPlayer({ slotId, playerName }) {
  const { attributes, listeners, setNodeRef: dragRef, transform } = useDraggable({
    id: slotId
  });
  const { setNodeRef: dropRef, isOver } = useDroppable({ id: slotId });
  const combinedRef = useCallback(
    (node) => {
      dragRef(node);
      dropRef(node);
    },
    [dragRef, dropRef]
  );
  const style = {
    transform: CSS.Translate.toString(transform),
    border: isOver ? "2px solid green" : "1px solid #bbb",
    padding: "8px",
    minWidth: "120px",
    backgroundColor: "#fff",
    cursor: "move"
  };
  return (
    <Box ref={combinedRef} style={style} {...listeners} {...attributes}>
      <Typography variant="body2" noWrap>
        {playerName}
      </Typography>
    </Box>
  );
}

function AutocompleteRow({ pId, originalId, allMembers, onSelect }) {
  const [value, setValue] = useState(null);
  const handleChange = useCallback(
    (_, newVal) => {
      setValue(newVal);
      if (newVal) onSelect(newVal);
    },
    [onSelect]
  );
  return (
    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
      <Typography sx={{ mr: 1 }}>Select a sub:</Typography>
      <Autocomplete
        sx={{ width: 250 }}
        size="small"
        value={value}
        onChange={handleChange}
        options={allMembers}
        getOptionLabel={(option) =>
          option ? `${option.firstName} ${option.lastName}` : "Unknown Member"
        }
        renderInput={(params) => <TextField {...params} label="New Member" />}
      />
    </Box>
  );
}

function PlayerRow({
  pId,
  memObj,
  isSub,
  attendanceMap,
  showContactMap,
  notesMap,
  toggleAttendance,
  toggleContactInfo,
  handleNoteChange,
  saveNoteToFirestore,
  formatPhoneNumber,
  onRemove
}) {
  const memId = memObj.id;
  const present = !!attendanceMap[memId];
  const contactShowing = !!showContactMap[memId];
  const phone = formatPhoneNumber(memObj.phoneNumber);
  const noteKey = `${pId}_${memId}`;
  const noteVal = notesMap[noteKey] || "";
  const nameLabel = isSub
    ? `${memObj.firstName} ${memObj.lastName} (Sub)`
    : `${memObj.firstName} ${memObj.lastName}`;
  const onToggleAttendance = useCallback(() => toggleAttendance(memId), [toggleAttendance, memId]);
  const onToggleContact = useCallback(() => toggleContactInfo(memId), [toggleContactInfo, memId]);
  const onNoteChange = useCallback(
    (evt) => handleNoteChange(pId, memId, evt.target.value),
    [handleNoteChange, pId, memId]
  );
  const onNoteBlur = useCallback(() => saveNoteToFirestore(pId, memId), [saveNoteToFirestore, pId, memId]);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mt: 1,
        color: present ? "green" : "red"
      }}
    >
      <Tooltip title="Remove Player">
        <IconButton size="small" color="error" onClick={onRemove}>
          <CloseIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Present/Absent">
        <Checkbox checked={present} onChange={onToggleAttendance} />
      </Tooltip>
      <Typography sx={{ width: 110 }} noWrap>
        {nameLabel}
      </Typography>
      {contactShowing && (
        <Box>
          {phone && <Typography variant="body2">Phone: {phone}</Typography>}
          {memObj.email && <Typography variant="body2">Email: {memObj.email}</Typography>}
        </Box>
      )}
      <Tooltip title="Contact Info">
        <IconButton size="small" onClick={onToggleContact}>
          <InfoIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <TextField
        label="Notes"
        variant="outlined"
        size="small"
        sx={{ width: 300 }}
        value={noteVal}
        onChange={onNoteChange}
        onBlur={onNoteBlur}
      />
    </Box>
  );
}

function PlayerOrAutocomplete(props) {
  const {
    pId,
    memObj,
    removedMap,
    replacedMap,
    allMembers,
    attendanceMap,
    showContactMap,
    notesMap,
    toggleAttendance,
    toggleContactInfo,
    handleNoteChange,
    saveNoteToFirestore,
    formatPhoneNumber,
    handleRemove,
    handleSelectReplacement,
    membersMap
  } = props;
  const memId = memObj.id;
  const onRemove = useCallback(() => {
    handleRemove(pId, memObj);
  }, [handleRemove, pId, memObj]);
  const onSelectReplacement = useCallback(
    (newMember) => {
      handleSelectReplacement(pId, memId, newMember);
    },
    [handleSelectReplacement, pId, memId]
  );
  if (removedMap[memId] && !replacedMap[memId]) {
    return (
      <AutocompleteRow
        pId={pId}
        originalId={memId}
        allMembers={allMembers}
        onSelect={onSelectReplacement}
      />
    );
  }
  if (replacedMap[memId]) {
    const newId = replacedMap[memId];
    const subObj = membersMap[newId] || {
      id: newId,
      firstName: "???",
      lastName: "???",
      phoneNumber: "",
      email: ""
    };
    return (
      <PlayerRow
        pId={pId}
        memObj={subObj}
        isSub
        attendanceMap={attendanceMap}
        showContactMap={showContactMap}
        notesMap={notesMap}
        toggleAttendance={toggleAttendance}
        toggleContactInfo={toggleContactInfo}
        handleNoteChange={handleNoteChange}
        saveNoteToFirestore={saveNoteToFirestore}
        formatPhoneNumber={formatPhoneNumber}
        onRemove={() => handleRemove(pId, memObj)}
      />
    );
  }
  if (removedMap[memId]) return null;
  return (
    <PlayerRow
      pId={pId}
      memObj={memObj}
      isSub={false}
      attendanceMap={attendanceMap}
      showContactMap={showContactMap}
      notesMap={notesMap}
      toggleAttendance={toggleAttendance}
      toggleContactInfo={toggleContactInfo}
      handleNoteChange={handleNoteChange}
      saveNoteToFirestore={saveNoteToFirestore}
      formatPhoneNumber={formatPhoneNumber}
      onRemove={onRemove}
    />
  );
}

// --- Main Component: RunPennant ---
export default function RunPennant() {
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    now.setDate(now.getDate());
    return now;
  });
  const [pennantsForDay, setPennantsForDay] = useState([]);
  const [loading, setLoading] = useState(false);

  const [membersMap, setMembersMap] = useState({});
  const [allMembers, setAllMembers] = useState([]);
  const [fillInRequiredMap, setFillInRequiredMap] = useState({});
  const [byeFillInMap, setByeFillInMap] = useState({});
  const [attendanceMap, setAttendanceMap] = useState({});
  const [showContactMap, setShowContactMap] = useState({});
  const [notesMap, setNotesMap] = useState({});
  const [drawsMap, setDrawsMap] = useState({});
  const [originalDrawsMap, setOriginalDrawsMap] = useState({});
  const [teamsByPennant, setTeamsByPennant] = useState({});

  const [dayRemovalsMap, setDayRemovalsMap] = useState({});
  const [replacementMap, setReplacementMap] = useState({});

  const [showFillInsDialog, setShowFillInsDialog] = useState(false);
  const [fillInsForSelectedPennant, setFillInsForSelectedPennant] = useState([]);
  const [selectedPennantForFillIns, setSelectedPennantForFillIns] = useState(null);

  const [scoresEnteredMap, setScoresEnteredMap] = useState({});
  const [openScoreSheet, setOpenScoreSheet] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [selectedPennant, setSelectedPennant] = useState(null);

  // Court numbers from Bookings.
  const [courtNumbersMap, setCourtNumbersMap] = useState({});

  // Fetch court numbers for currentDate.
  useEffect(() => {
    async function fetchCourtNumbers() {
      const dateStr = currentDate.toISOString().split("T")[0];
      const q = query(collection(db, "Bookings"), where("date", "==", dateStr));
      const snap = await getDocs(q);
      const newCourtNumbers = {};
      snap.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.drawMatchId) {
          newCourtNumbers[data.drawMatchId] = data.courtNumber;
        }
      });
      setCourtNumbersMap(newCourtNumbers);
    }
    fetchCourtNumbers();
  }, [currentDate]);

  let scoreSheetContent;
  if (!selectedPennant || !selectedWeek) {
    scoreSheetContent = <Typography>Loading Score Sheet...</Typography>;
  } else if (selectedPennant.participantType === "Teams") {
    scoreSheetContent = <TeamsScoreSheet pennantId={selectedPennant.id} weekData={selectedWeek} />;
  } else {
    scoreSheetContent = <IndividualsScoreSheet pennantId={selectedPennant.id} weekData={selectedWeek} />;
  }

  const handlePreviousDay = useCallback(() => {
    setCurrentDate((prev) => {
      const copy = new Date(prev);
      copy.setDate(copy.getDate() - 1);
      return copy;
    });
  }, []);

  const handleNextDay = useCallback(() => {
    setCurrentDate((prev) => {
      const copy = new Date(prev);
      copy.setDate(copy.getDate() + 1);
      return copy;
    });
  }, []);

  useEffect(() => {
    async function fetchAllMembers() {
      try {
        const snap = await getDocs(collection(db, "members"));
        const map = {};
        const list = [];
        snap.forEach((docSnap) => {
          const d = docSnap.data();
          const mem = {
            id: docSnap.id,
            firstName: d.firstName || "Unknown",
            lastName: d.lastName || "Member",
            phoneNumber: d.phoneNumber || "",
            email: d.email || ""
          };
          map[docSnap.id] = mem;
          list.push(mem);
        });
        setMembersMap(map);
        setAllMembers(list);
      } catch (err) {
        console.error("Error fetching global members:", err);
      }
    }
    fetchAllMembers();
  }, []);

  useEffect(() => {
    async function fetchScoresStatus() {
      const dateStr = formatDate(currentDate);
      const statusPromises = pennantsForDay.map(async (pennant) => {
        const pId = pennant.id;
        const drawInfo = drawsMap[pId];
        const scheduledMatches =
          drawInfo && Array.isArray(drawInfo.weeks)
            ? drawInfo.weeks
                .filter((w) => w.date === dateStr)
                .reduce(
                  (sum, w) => sum + (Array.isArray(w.matches) ? w.matches.length : 0),
                  0
                )
            : 0;
        let matchesTotalDocs = 0;
        let matchesEntered = 0;
        const qMatches = query(
          collection(db, "Matches"),
          where("pennantId", "==", pId),
          where("weekDateStr", "==", dateStr)
        );
        const snap = await getDocs(qMatches);
        matchesTotalDocs = snap.size;
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          if (
            (data.team1 && data.team1.matchPoints > 0) ||
            (data.team2 && data.team2.matchPoints > 0)
          ) {
            matchesEntered += 1;
          }
        });
        const finalTotal = scheduledMatches > 0 ? scheduledMatches : matchesTotalDocs;
        return { pId, matchesEntered, matchesTotal: finalTotal };
      });
      const results = await Promise.all(statusPromises);
      const statusObj = results.reduce((acc, { pId, matchesEntered, matchesTotal }) => {
        acc[pId] = { matchesEntered, matchesTotal };
        return acc;
      }, {});
      setScoresEnteredMap(statusObj);
    }
    if (pennantsForDay.length > 0 && Object.keys(drawsMap).length > 0) {
      fetchScoresStatus();
    }
  }, [pennantsForDay, drawsMap, currentDate]);

  useEffect(() => {
    fetchPennantsForDay();
  }, [currentDate]);

  async function fetchPennantsForDay() {
    try {
      setLoading(true);
      const dayName = currentDate.toLocaleString("en-US", { weekday: "long" });
      const pennantsQ = query(collection(db, "Pennants"), where("pennantDay", "==", dayName));
      const snap = await getDocs(pennantsQ);
      if (snap.empty) {
        setPennantsForDay([]);
        setLoading(false);
        return;
      }
      const results = snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      setPennantsForDay(results);
      await Promise.all([fetchFillIns(results), checkByesInDraws(results), fetchDrawsForPennants(results), fetchTeamsForPennants(results)]);
    } catch (err) {
      console.error("Error fetching Pennants:", err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchFillIns(pennants) {
    const fillInPromises = pennants.map(async (p) => {
      const cRef = collection(db, "Pennants", p.id, "fillInRequired");
      const snap = await getDocs(cRef);
      const fillIns = snap.docs.map((d) => ({
        id: d.id,
        docRef: d.ref,
        ...d.data()
      }));
      fillIns.forEach((item) => {
        const originalId = item.memberId;
        const subId = item.subId || null;
        setDayRemovalsMap((prev) => {
          const existing = prev[p.id] || {};
          return { ...prev, [p.id]: { ...existing, [originalId]: true } };
        });
        if (subId) {
          setReplacementMap((prev) => {
            const exist2 = prev[p.id] || {};
            return { ...prev, [p.id]: { ...exist2, [originalId]: subId } };
          });
        }
      });
      return { pennantId: p.id, fillIns };
    });
    const arr = await Promise.all(fillInPromises);
    const map = {};
    arr.forEach(({ pennantId, fillIns }) => {
      map[pennantId] = fillIns;
    });
    setFillInRequiredMap(map);
  }

  async function checkByesInDraws(pennants) {
    const dateStr = formatDate(currentDate);
    const byeMap = {};
    const tasks = pennants.map(async (pennant) => {
      const dSnap = await getDocs(collection(db, "Pennants", pennant.id, "draws"));
      const fillInsForThisPennant = [];
      dSnap.forEach((docSnap) => {
        const data = docSnap.data();
        if (!Array.isArray(data.weeks)) return;
        data.weeks.forEach((w) => {
          if (w.date === dateStr) {
            (w.matches || []).forEach((m) => {
              const t1Name = m.team1?.name?.toLowerCase() || "";
              const t2Name = m.team2?.name?.toLowerCase() || "";
              if (t1Name === "bye" || t2Name === "bye") {
                fillInsForThisPennant.push({
                  reason: "Bye in Draw",
                  date: w.date,
                  matchInfo: m
                });
              }
            });
          }
        });
      });
      byeMap[pennant.id] = fillInsForThisPennant;
    });
    await Promise.all(tasks);
    setByeFillInMap(byeMap);
  }

  async function fetchDrawsForPennants(pennants) {
    const tasks = pennants.map(async (p) => {
      const snap = await getDocs(collection(db, "Pennants", p.id, "draws"));
      let docId = null;
      let weeks = [];
      snap.forEach((docSnap) => {
        const dData = docSnap.data();
        if (!Array.isArray(dData.weeks)) return;
        docId = docSnap.id;
        weeks = dData.weeks;
      });
      return { pennantId: p.id, docId, weeks };
    });
    const arr = await Promise.all(tasks);
    const map = {};
    arr.forEach((res) => {
      map[res.pennantId] = res;
    });
    setDrawsMap(map);
    setOriginalDrawsMap(JSON.parse(JSON.stringify(map)));
  }

  async function fetchTeamsForPennants(pennants) {
    const tasks = pennants.map(async (p) => {
      const colRef = collection(db, "Pennants", p.id, "teams");
      const tSnap = await getDocs(colRef);
      if (tSnap.empty) return [p.id, {}];
      const innerMap = {};
      tSnap.forEach((docSnap) => {
        innerMap[docSnap.id] = docSnap.data();
      });
      return [p.id, innerMap];
    });
    const entries = await Promise.all(tasks);
    const outerMap = Object.fromEntries(entries);
    setTeamsByPennant(outerMap);
  }

  async function refreshFillInsForPennant(pId) {
    const target = pennantsForDay.find((p) => p.id === pId);
    if (target) {
      await fetchFillIns([target]);
    }
  }

  async function refreshDrawsForPennant(pId) {
    const target = pennantsForDay.find((p) => p.id === pId);
    if (target) {
      await fetchDrawsForPennants([target]);
    }
  }

  const handleOpenFillInsDialogForPennant = useCallback(
    async (pId) => {
      try {
        const cRef = collection(db, "Pennants", pId, "availableFillIns");
        const snap = await getDocs(cRef);
        const results = [];
        if (!snap.empty) {
          snap.forEach((docSnap) => {
            results.push({
              docId: docSnap.id,
              ...docSnap.data()
            });
          });
        }
        setFillInsForSelectedPennant(results);
        const pennant = pennantsForDay.find((p) => p.id === pId);
        setSelectedPennantForFillIns(pennant);
        setShowFillInsDialog(true);
      } catch (error) {
        console.error("Error fetching available fill-ins:", error);
      }
    },
    [pennantsForDay]
  );

  const onDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const fromData = parseSlotId(active.id);
      const toData = parseSlotId(over.id);
      if (!fromData || !toData) return;
      if (fromData.pId !== toData.pId) {
        console.warn("Ignoring cross-pennant drag");
        return;
      }
      const pDraw = drawsMap[fromData.pId];
      if (!pDraw || !pDraw.weeks) return;
      const updatedWeeks = [...pDraw.weeks];
      const fromWeek = { ...updatedWeeks[fromData.weekIndex] };
      const toWeek = { ...updatedWeeks[toData.weekIndex] };
      if (!fromWeek || !toWeek) return;
      const fromMatch = { ...fromWeek.matches[fromData.matchIndex] };
      const toMatch = { ...toWeek.matches[toData.matchIndex] };
      const temp = fromMatch[fromData.side];
      fromMatch[fromData.side] = toMatch[toData.side];
      toMatch[toData.side] = temp;
      fromWeek.matches[fromData.matchIndex] = fromMatch;
      toWeek.matches[toData.matchIndex] = toMatch;
      updatedWeeks[fromData.weekIndex] = fromWeek;
      updatedWeeks[toData.weekIndex] = toWeek;
      setDrawsMap((prev) => ({
        ...prev,
        [fromData.pId]: { ...pDraw, weeks: updatedWeeks }
      }));
    },
    [drawsMap]
  );

  const saveDrawChanges = useCallback(async (pId, newDrawObj) => {
    const { docId } = newDrawObj;
    if (!docId) return;
    try {
      const docRef = doc(db, "Pennants", pId, "draws", docId);
      await updateDoc(docRef, {
        weeks: newDrawObj.weeks,
        updatedAt: new Date()
      });
      console.log("Saved updated draw");
    } catch (err) {
      console.error("Error saving updated draw:", err);
    }
  }, []);

  const handleResetMatch = useCallback(
    (pId, weekIndex, matchIndex) => {
      const orig = originalDrawsMap[pId];
      if (!orig) return;
      const resetMatch = orig.weeks[weekIndex].matches[matchIndex];
      setDrawsMap((prev) => {
        const pDraw = prev[pId];
        if (!pDraw || !pDraw.weeks) return prev;
        const newWeeks = pDraw.weeks.map((w, wi) => {
          if (wi !== weekIndex) return w;
          return {
            ...w,
            matches: w.matches.map((m, mi) =>
              mi === matchIndex ? { ...resetMatch } : m
            )
          };
        });
        const newState = { ...prev, [pId]: { ...pDraw, weeks: newWeeks } };
        saveDrawChanges(pId, { docId: pDraw.docId, weeks: newWeeks });
        return newState;
      });
    },
    [originalDrawsMap, saveDrawChanges]
  );

  const handleCourtNumberChange = useCallback((pId, wIndex, mIndex, newValue) => {
    setDrawsMap((prev) => {
      const pDraw = prev[pId];
      if (!pDraw || !pDraw.weeks) return prev;
      const newWeeks = pDraw.weeks.map((week, idx) => {
        if (idx !== wIndex) return week;
        const newMatches = week.matches.map((mtch, mIdx) => {
          if (mIdx !== mIndex) return mtch;
          return { ...mtch, courtNumber: newValue };
        });
        return { ...week, matches: newMatches };
      });
      return { ...prev, [pId]: { ...pDraw, weeks: newWeeks } };
    });
  }, []);

  const saveCourtNumberToBookings = useCallback(
    async (pId, wIndex, mIndex, courtNumber, drawMatchId) => {
      if (!drawMatchId) {
        console.error("Cannot save court number: no draw match id provided for", { pId, wIndex, mIndex });
        return;
      }
      const bookingRef = doc(db, "Bookings", drawMatchId);
      try {
        await setDoc(
          bookingRef,
          {
            date: currentDate.toISOString().split("T")[0],
            pennantId: pId,
            courtNumber,
            drawMatchId,
            updatedAt: serverTimestamp()
          },
          { merge: true }
        );
        console.log("Court number saved");
      } catch (err) {
        console.error("Error saving court number to Bookings:", err);
      }
    },
    [currentDate]
  );

  const handleCourtNumberBlur = useCallback(
    (pId, wIndex, mIndex, e) => {
      const inputValue = e.target.value;
      const drawMatchId = getDrawMatchId({ pId, weekIndex: wIndex, matchIndex: mIndex });
      setDrawsMap((prev) => {
        const pDraw = { ...prev[pId] };
        if (pDraw && pDraw.weeks && pDraw.weeks[wIndex] && pDraw.weeks[wIndex].matches[mIndex]) {
          const currentSavedValue = pDraw.weeks[wIndex].matches[mIndex].courtNumber;
          pDraw.weeks[wIndex].matches[mIndex].id = drawMatchId;
          pDraw.weeks[wIndex].matches[mIndex].courtNumber =
            inputValue !== "" ? inputValue : currentSavedValue;
        }
        return { ...prev, [pId]: pDraw };
      });
      saveCourtNumberToBookings(pId, wIndex, mIndex, inputValue, drawMatchId);
    },
    [saveCourtNumberToBookings]
  );

  const toggleAttendance = useCallback((memberId) => {
    setAttendanceMap((prev) => ({ ...prev, [memberId]: !prev[memberId] }));
  }, []);

  const toggleContactInfo = useCallback((memberId) => {
    setShowContactMap((prev) => ({ ...prev, [memberId]: !prev[memberId] }));
  }, []);

  function getNoteKey(pId, mId) {
    return `${pId}_${mId}`;
  }

  const handleNoteChange = useCallback((pId, mId, newVal) => {
    setNotesMap((prev) => ({ ...prev, [getNoteKey(pId, mId)]: newVal }));
  }, []);

  const saveNoteToFirestore = useCallback(
    async (pId, mId) => {
      const note = notesMap[getNoteKey(pId, mId)] || "";
      if (!note) return;
      try {
        const dateStr = formatDate(currentDate);
        const dateDocId = dateStr.replace(/\//g, "-");
        const memRef = doc(db, "Pennants", pId, "dayNotes", dateDocId, "members", mId);
        await setDoc(memRef, { note, updatedAt: new Date() });
        console.log("Saved note");
      } catch (err) {
        console.error("Error saving note:", err);
      }
    },
    [currentDate, notesMap]
  );

  const createFillInDocIfNeeded = useCallback(
    async (pId, memObj) => {
      const memId = memObj.id;
      const existingArr = fillInRequiredMap[pId] || [];
      const found = existingArr.find((f) => f.memberId === memId);
      if (found) {
        console.log("fillIn doc already exists");
        return;
      }
      try {
        const cRef = collection(db, "Pennants", pId, "fillInRequired");
        await addDoc(cRef, {
          reason: "Member not coming",
          createdAt: new Date(),
          memberId: memId,
          memberName: `${memObj.firstName} ${memObj.lastName}`,
          subId: null,
          fillInName: null
        });
        console.log("Created fillInRequired");
      } catch (err) {
        console.error("Error creating fillIn doc:", err);
      }
    },
    [fillInRequiredMap]
  );

  const handleRemoveForDay = useCallback(
    async (pId, memObj) => {
      const memId = memObj.id;
      const fillIns = fillInRequiredMap[pId] || [];
      const updatePromises = [];
      fillIns.forEach((item) => {
        const originalId = memObj.originalId || memObj.id;
        if (item.memberId === originalId && item.subId) {
          updatePromises.push(
            updateDoc(item.docRef, { subId: null, fillInName: null })
              .then(() => console.log("Removed sub occupant"))
              .catch((err) => console.error("Error removing sub occupant:", err))
          );
          setReplacementMap((prev) => {
            const pMap = { ...(prev[pId] || {}) };
            delete pMap[originalId];
            return { ...prev, [pId]: pMap };
          });
        }
      });
      await Promise.all(updatePromises);
      await refreshFillInsForPennant(pId);
      const occupantDoc = fillIns.find((f) => f.memberId === memObj.id);
      if (!occupantDoc) {
        await createFillInDocIfNeeded(pId, memObj);
        await refreshFillInsForPennant(pId);
      }
      setAttendanceMap((prev) => ({ ...prev, [memObj.id]: false }));
      setDayRemovalsMap((prev) => {
        const existing = prev[pId] || {};
        return { ...prev, [pId]: { ...existing, [memObj.id]: true } };
      });
    },
    [fillInRequiredMap, refreshFillInsForPennant, createFillInDocIfNeeded]
  );

  const handleSelectReplacement = useCallback(
    async (pId, originalId, newMember) => {
      if (!newMember) return;
      const newId = newMember.id;
      setReplacementMap((prev) => {
        const existing = prev[pId] || {};
        return { ...prev, [pId]: { ...existing, [originalId]: newId } };
      });
      const fillIns = fillInRequiredMap[pId] || [];
      const item = fillIns.find((f) => f.memberId === originalId);
      if (!item) {
        console.warn("No fillIn doc for originalId:", originalId);
        return;
      }
      try {
        await updateDoc(item.docRef, {
          subId: newId,
          fillInName: `${newMember.firstName} ${newMember.lastName}`
        });
        console.log("Updated fillIn");
        await refreshFillInsForPennant(pId);
      } catch (err) {
        console.error("Error updating fillIn doc:", err);
      }
    },
    [fillInRequiredMap, refreshFillInsForPennant]
  );

  const handleScoresClickForSection = useCallback(
    (pennant, weekObj) => {
      const dateStr = formatDate(currentDate);
      const weekDataObj = {
        id: weekObj.id || "default-id",
        date: dateStr,
        week: weekObj.week || "",
        matches: weekObj.matches || [],
        section: weekObj.section || ""
      };
      setSelectedWeek(weekDataObj);
      setSelectedPennant(pennant);
      setOpenScoreSheet(true);
    },
    [currentDate]
  );

  const handlePrintClickForSection = useCallback(
    (pennant, weekObj) => {
      const dateStr = formatDate(currentDate);
      const fullIndex =
        weekObj.fullIndex !== undefined
          ? weekObj.fullIndex
          : (drawsMap[pennant.id]?.weeks?.findIndex((w) => w.id === weekObj.id)) ?? 0;
      const mergedMatches = (weekObj.matches || []).map((match, mIndex) => {
        const drawMatchId = `pennant-${pennant.id}-week-${fullIndex}-match-${mIndex}`;
        return {
          ...match,
          courtNumber: courtNumbersMap[drawMatchId] || match.courtNumber || "",
          id: drawMatchId
        };
      });
      const weekDataObj = {
        ...weekObj,
        date: dateStr,
        matches: mergedMatches
      };
      const weekDataStr = encodeURIComponent(JSON.stringify(weekDataObj));
      const url = `/printable-scoresheet?pennantId=${pennant.id}&weekData=${weekDataStr}`;
      window.open(url, "_blank");
    },
    [currentDate, courtNumbersMap, drawsMap]
  );

  const handleScoresClick = useCallback(
    (pennant) => {
      const pId = pennant.id;
      const dateStr = formatDate(currentDate);
      const drawsForPennant = drawsMap[pId] || {};
      const matchedWeeks = drawsForPennant.weeks || [];
      const matchedWeek = matchedWeeks[0] || {};
      const weekDataObj = {
        id: matchedWeek.id || "default-id",
        date: dateStr,
        week: matchedWeek.week || "",
        matches: matchedWeek.matches || [],
        section: matchedWeek.section || ""
      };
      setSelectedWeek(weekDataObj);
      setSelectedPennant(pennant);
      setOpenScoreSheet(true);
    },
    [currentDate, drawsMap]
  );

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={handlePreviousDay}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" gutterBottom sx={{ mx: 2 }}>
          Pennants for {currentDate.toDateString()}
        </Typography>
        <IconButton onClick={handleNextDay}>
          <ArrowForwardIcon />
        </IconButton>
      </Box>

      {pennantsForDay.length === 0 && (
        <Typography>No pennants scheduled for this day.</Typography>
      )}

      <DndContext onDragEnd={onDragEnd}>
        {pennantsForDay.map((pennant) => {
          const pId = pennant.id;
          const { matchesEntered = 0, matchesTotal = 0 } = scoresEnteredMap[pId] || {};
          const rawFills = fillInRequiredMap[pId] || [];
          const byeFills = byeFillInMap[pId] || [];
          const totalFillIns = [...rawFills, ...byeFills];
          const allSections = Object.values(pennant.members || {});
          const allMemberIds = allSections.flat();
          const totalCount = allMemberIds.length;
          const presentCount = allMemberIds.filter((mid) => attendanceMap[mid]).length;
          const { docId, weeks } = drawsMap[pId] || {};
          const removedMap = dayRemovalsMap[pId] || {};
          const replacedMap = replacementMap[pId] || {};

          return (
            <Accordion key={pId}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ flex: 1, fontWeight: "bold" }}>
                  {pennant.sport} {pennant.name}
                </Typography>
                {totalFillIns.length > 0 && (
                  <Tooltip title="Fill-ins needed">
                    <Typography color="warning.main">
                      {totalFillIns.length} Fill-Ins
                    </Typography>
                  </Tooltip>
                )}
                <Tooltip title="Scores entered">
                  <Typography sx={{ ml: 2 }} color={matchesEntered === matchesTotal ? "green" : "red"}>
                    {matchesEntered}/{matchesTotal} Scores Entered
                  </Typography>
                </Tooltip>
                {allMemberIds.length > 0 && (
                  <Tooltip title="Members in attendance">
                    <Typography color="primary" sx={{ ml: 2 }}>
                      {presentCount}/{totalCount} Present
                    </Typography>
                  </Tooltip>
                )}
              </AccordionSummary>
              <AccordionDetails>
                {(!weeks || weeks.length === 0) ? (
                  <Typography sx={{ mb: 2 }}>
                    No matches found for today in the draw.
                  </Typography>
                ) : (
                  weeks
                    .map((week, index) => ({ ...week, fullIndex: index }))
                    .filter(({ date }) => date === formatDate(currentDate))
                    .map(({ fullIndex, ...weekObj }) => (
                      <Box key={`${weekObj.id}-${fullIndex}`} sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Week {weekObj.week} – Section {weekObj.section}
                        </Typography>
                        <Box sx={{ mb: 3, display: "flex", gap: 1 }}>
                          <FillInsButton pId={pId} onOpenFillIns={handleOpenFillInsDialogForPennant} />
                          <ScoresButton onScores={() => handleScoresClickForSection(pennant, weekObj)} />
                          <PrintButton pId={pId} onPrint={() => handlePrintClickForSection(pennant, weekObj)} />
                        </Box>
                        {(weekObj.matches || []).map((match, mIndex) => {
                          const t1 = match.team1 || {};
                          const t2 = match.team2 || {};
                          const t1Name = t1.name || "Unknown";
                          const t2Name = t2.name || "Unknown";
                          const t1SlotId = buildSlotId({ pId, weekIndex: fullIndex, matchIndex: mIndex, side: "team1" });
                          const t2SlotId = buildSlotId({ pId, weekIndex: fullIndex, matchIndex: mIndex, side: "team2" });
                          const drawMatchId = `pennant-${pId}-week-${fullIndex}-match-${mIndex}`;
                          const courtNumber = courtNumbersMap[drawMatchId] || match.courtNumber || "";
                          return (
                            <Card key={mIndex} variant="outlined" sx={{ mb: 2, p: 1, backgroundColor: "#fafafa" }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                <Typography sx={{ fontWeight: "bold" }}>
                                  {t1Name} vs {t2Name}
                                </Typography>
                                <Tooltip title="Rollback to original players">
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleResetMatch(pId, fullIndex, mIndex)}
                                  >
                                    <ReplayIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Box sx={{ mb: 2 }}>
                                <TextField
                                  label="Court Number"
                                  variant="outlined"
                                  size="small"
                                  value={courtNumber}
                                  onChange={(e) =>
                                    handleCourtNumberChange(pId, fullIndex, mIndex, e.target.value)
                                  }
                                  onBlur={(e) => {
                                    handleCourtNumberBlur(pId, fullIndex, mIndex, e);
                                  }}
                                  sx={{ width: 140 }}
                                />
                              </Box>
                              <Box sx={{ display: "flex", gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ textDecoration: "underline" }}>{t1Name}</Typography>
                                  <DnDPlayer slotId={t1SlotId} playerName={t1Name} />
                                  {getPlayersForSide(pennant, t1, teamsByPennant, membersMap, fullIndex, mIndex).map((memObj) => (
                                    <PlayerOrAutocomplete
                                      key={memObj.id}
                                      pId={pId}
                                      memObj={memObj}
                                      removedMap={dayRemovalsMap[pId] || {}}
                                      replacedMap={replacementMap[pId] || {}}
                                      allMembers={allMembers}
                                      attendanceMap={attendanceMap}
                                      showContactMap={showContactMap}
                                      notesMap={notesMap}
                                      toggleAttendance={toggleAttendance}
                                      toggleContactInfo={toggleContactInfo}
                                      handleNoteChange={handleNoteChange}
                                      saveNoteToFirestore={saveNoteToFirestore}
                                      formatPhoneNumber={formatPhoneNumber}
                                      handleRemove={handleRemoveForDay}
                                      handleSelectReplacement={handleSelectReplacement}
                                      membersMap={membersMap}
                                    />
                                  ))}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ textDecoration: "underline" }}>{t2Name}</Typography>
                                  <DnDPlayer slotId={t2SlotId} playerName={t2Name} />
                                  {getPlayersForSide(pennant, t2, teamsByPennant, membersMap, fullIndex, mIndex).map((memObj) => (
                                    <PlayerOrAutocomplete
                                      key={memObj.id}
                                      pId={pId}
                                      memObj={memObj}
                                      removedMap={dayRemovalsMap[pId] || {}}
                                      replacedMap={replacementMap[pId] || {}}
                                      allMembers={allMembers}
                                      attendanceMap={attendanceMap}
                                      showContactMap={showContactMap}
                                      notesMap={notesMap}
                                      toggleAttendance={toggleAttendance}
                                      toggleContactInfo={toggleContactInfo}
                                      handleNoteChange={handleNoteChange}
                                      saveNoteToFirestore={saveNoteToFirestore}
                                      formatPhoneNumber={formatPhoneNumber}
                                      handleRemove={handleRemoveForDay}
                                      handleSelectReplacement={handleSelectReplacement}
                                      membersMap={membersMap}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            </Card>
                          );
                        })}
                      </Box>
                    ))
                )}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </DndContext>

      <Dialog
        open={showFillInsDialog}
        onClose={() => setShowFillInsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Available Fill-Ins{" "}
          {selectedPennantForFillIns && `for ${selectedPennantForFillIns.name} ${selectedPennantForFillIns.sport}`}
        </DialogTitle>
        <DialogContent dividers>
          {fillInsForSelectedPennant.length === 0 ? (
            <Typography>No one has registered as a fill-in for this pennant.</Typography>
          ) : (
            <List>
              {fillInsForSelectedPennant.map((fi) => {
                const name = fi.memberName || "Unknown Member";
                const phone = fi.phone || "";
                const section = fi.requestedSection || "N/A";
                return (
                  <ListItem key={fi.docId} sx={{ mb: 1, alignItems: "flex-start" }}>
                    <ListItemText
                      primary={`${name} (${fi.sport || "?"}) - ${fi.pennantName || ""}`}
                      secondary={
                        <>
                          <Typography variant="body2">Section: {section}</Typography>
                          <Typography variant="body2">
                            Phone: {phone.startsWith("+61") ? `0${phone.slice(3)}` : phone}
                          </Typography>
                          <Typography variant="body2">Email: {fi.email || ""}</Typography>
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setShowFillInsDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openScoreSheet && selectedPennant !== null}
        onClose={() => {
          setOpenScoreSheet(false);
          setSelectedWeek(null);
          setSelectedPennant(null);
        }}
        maxWidth="md"
        fullWidth
      >
        {scoreSheetContent}
      </Dialog>
    </Box>
  );
}
